import DOMPurify from 'dompurify'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { DocumentLayout } from '@/modules/documents/components/layout/DocumentLayout'
import { StAwellHtml } from '@/modules/task/components/message-detail/MessageDetail.styled'
import { RelatedTriggers } from '@/modules/timeline/components/related-triggers/RelatedTriggers'
import { MessageAttachmentItem } from '../../message-attachment-item/MessageAttachmentItem'

import type { PatientViewDocumentMessage } from '@/modules/documents/types/patient-documents.types'
import type { InfoMessage, MessageAttachment } from '../../../types/message.types'

function MessageDetailAttachments({
  attachments,
  onAttachmentOpen,
}: {
  attachments: MessageAttachment[]
  onAttachmentOpen?: () => void
}) {
  const theme = useTheme()
  return (
    <FlexColumn gap={theme.UI.Spacing.Space2}>
      {attachments.map((attachment) => (
        <MessageAttachmentItem
          key={attachment.name}
          attachment={attachment}
          source='message'
          onAttachmentOpen={onAttachmentOpen}
        />
      ))}
    </FlexColumn>
  )
}

export type InfoMessageDetailProps = (
  | {
      document: PatientViewDocumentMessage
    }
  | {
      message: InfoMessage
    }
) & {
  onAttachmentOpen?: () => void
}

export const InfoMessageDetail = ({ ...props }: InfoMessageDetailProps) => {
  const document =
    'document' in props
      ? props.document
      : {
          id: props.message.id,
          display: props.message.title,
          type: 'Message',
        }

  const message = 'message' in props ? props.message : props.document.message

  if (!message) {
    return null
  }

  return (
    <DocumentLayout>
      <DocumentLayout.Header document={document} />
      <DocumentLayout.Content>
        <StContent>
          {message.format === 'html' ? (
            <StAwellHtml
              // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(message.body),
              }}
            />
          ) : (
            <Markdown content={message.body} />
          )}

          {message.attachments?.length ? (
            <MessageDetailAttachments attachments={message.attachments} onAttachmentOpen={props.onAttachmentOpen} />
          ) : null}
        </StContent>

        {/* Related triggers */}
        {message.trigger ? <RelatedTriggers trigger={message.trigger} /> : null}
      </DocumentLayout.Content>
    </DocumentLayout>
  )
}

const StContent = styled(FlexColumn)`
  flex: 1;
`
